<template>
  <div class="ncrm-privacy-dashboard">
    <div class="ncrm-privacy-dashboard__toolbar">
      <NcrmSegmentedControl
        :model-value="view"
        :items="viewItems"
        :aria-label="$t(`${copyKey}.viewsLabel`)"
        @update:model-value="$emit('update-view', $event)"
      />
      <span v-if="dashboard" class="ncrm-privacy-card__muted">
        {{ $t(`${copyKey}.period`, { days: periodDays, at: formatDateTime(dashboard.generated_at) }) }}
      </span>
    </div>

    <v-skeleton-loader v-if="loading" type="card@2, list-item-two-line@4" />
    <v-alert v-else-if="errorMessage" :type="forbidden ? 'warning' : 'error'">
      {{ errorMessage }}
      <template v-if="!forbidden" #append>
        <v-btn variant="text" @click="fetchDashboard">{{ $t(`${copyKey}.retry`) }}</v-btn>
      </template>
    </v-alert>
    <template v-else-if="dashboard">
      <v-alert v-if="dashboard.unavailable.length" type="info" density="comfortable">
        {{ $t(`${copyKey}.partial`) }}
      </v-alert>

      <NcrmHomeKpiCards :items="recordKpis" @select="$emit('drill-down', $event.state)" />

      <div class="ncrm-privacy-dashboard__grid">
        <NcrmPrivacyPairsCard
          :by-state="dashboard.projections.by_state"
          :by-channel="dashboard.distributions.by_channel"
          :by-purpose="dashboard.distributions.by_purpose"
          :unavailable="dashboard.unavailable"
        />
        <div class="ncrm-privacy-dashboard__side">
          <NcrmPrivacyBreakdownCard
            :view="view"
            :by-state="subject.by_state"
            :total="subject.total"
            @drill-down="$emit('drill-down', $event)"
          />
          <NcrmPrivacyActivityCard
            :activity="dashboard.activity"
            :events-by-source="dashboard.distributions.events_by_source"
            :period-days="periodDays"
            :unavailable="dashboard.unavailable"
            @open-failed-sync="$emit('open-failed-sync')"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import NcrmHomeKpiCards from "@/components/ncrm/shared/NcrmHomeKpiCards.vue";
import NcrmSegmentedControl from "@/components/ncrm/shared/NcrmSegmentedControl.vue";
import { resolvePrivacyStatus } from "@/Utile/ncrmPrivacyStatus.mjs";
import NcrmPrivacyActivityCard from "./NcrmPrivacyActivityCard.vue";
import NcrmPrivacyBreakdownCard from "./NcrmPrivacyBreakdownCard.vue";
import NcrmPrivacyPairsCard from "./NcrmPrivacyPairsCard.vue";
import { fetchPrivacyDashboard } from "./privacyWorkspaceApi.mjs";
import { sharePercent } from "./privacyDashboardModel.mjs";
import { DRILL_DOWN_KPIS, SUBJECT_VIEWS } from "./privacyWorkspaceContracts.mjs";

const SUBJECT_KEY = Object.freeze({ persons: "person", companies: "company" });
const DAY_MS = 24 * 60 * 60 * 1000;
const KPI_COLORS = Object.freeze({
  evaluated: "primary",
  withdrawn_or_refused: "error",
  partial: "warning",
  // `undocumented` rămâne neutru în modul monitor (`GDPR-042`).
  undocumented: "neutral",
});

export default {
  name: "NcrmPrivacyDashboard",
  components: {
    NcrmHomeKpiCards,
    NcrmPrivacyActivityCard,
    NcrmPrivacyBreakdownCard,
    NcrmPrivacyPairsCard,
    NcrmSegmentedControl,
  },
  props: {
    view: { type: String, default: "persons" },
  },
  emits: ["update-view", "drill-down", "open-failed-sync"],
  data: () => ({ loading: false, errorMessage: "", forbidden: false, dashboard: null, requestSequence: 0 }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.workspace"; },
    viewItems() {
      return SUBJECT_VIEWS.map((value) => ({ value, label: this.$t(`${this.copyKey}.views.${value}`) }));
    },
    subject() { return this.dashboard?.subjects?.[SUBJECT_KEY[this.view]] || { total: 0, by_state: {} }; },
    periodDays() {
      const period = this.dashboard?.period;
      return period ? Math.round((new Date(period.to) - new Date(period.from)) / DAY_MS) : 30;
    },
    // Toate KPI-urile de nivel record sunt clicabile și deschid registrul pe
    // aceeași vedere; procentul arată cât cântărește starea în total.
    recordKpis() {
      return Object.entries(DRILL_DOWN_KPIS).map(([key, state]) => ({
        key,
        state,
        clickable: true,
        label: this.$t(`${this.copyKey}.kpis.${key === "evaluated" ? `evaluated.${this.view}` : key}`),
        value: state ? this.subject.by_state[state] : this.subject.total,
        hint: state
          ? this.$t(`${this.copyKey}.hints.share.${this.view}`, {
            pct: sharePercent(this.subject.by_state[state], this.subject.total),
          })
          : this.$t(`${this.copyKey}.hints.evaluated.${this.view}`),
        icon: key === "evaluated" ? "mdi-account-group-outline" : resolvePrivacyStatus(state).icon,
        color: KPI_COLORS[key],
      }));
    },
  },
  created() { this.fetchDashboard(); },
  methods: {
    async fetchDashboard() {
      const sequence = ++this.requestSequence;
      this.loading = true; this.errorMessage = ""; this.forbidden = false;
      try {
        const data = await fetchPrivacyDashboard();
        if (sequence !== this.requestSequence) return;
        this.dashboard = data;
      } catch (error) {
        if (sequence !== this.requestSequence) return;
        this.forbidden = Number(error?.response?.status) === 403;
        this.errorMessage = this.$t(`${this.copyKey}.${this.forbidden ? "noPermission" : "loadError"}`);
      } finally {
        if (sequence === this.requestSequence) this.loading = false;
      }
    },
    formatDateTime(value) {
      const date = new Date(value);
      if (!value || Number.isNaN(date.getTime())) return "—";
      const pad = (part) => String(part).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
/* `main.css` centrează tot `#app`; dashboard-ul este un spațiu de lucru, deci
   textul pornește de la stânga. */
.ncrm-privacy-dashboard { display: flex; flex-direction: column; gap: 20px; text-align: left; }
.ncrm-privacy-dashboard__toolbar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; }
.ncrm-privacy-dashboard__grid { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap: 16px; align-items: start; }
.ncrm-privacy-dashboard__side { display: flex; flex-direction: column; gap: 16px; }

/* Anatomia comună a cardurilor dashboard-ului, aplicată copiilor. */
.ncrm-privacy-dashboard :deep(.ncrm-privacy-card) {
  display: flex; flex-direction: column; gap: 18px; padding: 24px; border-radius: var(--app-radius-base);
  background: rgb(var(--v-theme-surface)); box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.ncrm-privacy-dashboard :deep(.ncrm-privacy-card--compact) { gap: 14px; padding: 22px; }
.ncrm-privacy-dashboard :deep(.ncrm-privacy-card__header) { display: flex; flex-direction: column; gap: 4px; }
.ncrm-privacy-dashboard :deep(.ncrm-privacy-card__title) { margin: 0; font-size: 17px; font-weight: 700; }
.ncrm-privacy-dashboard :deep(.ncrm-privacy-card__subtitle),
.ncrm-privacy-dashboard :deep(.ncrm-privacy-card__muted) { margin: 0; font-size: 13px; color: rgba(var(--v-theme-on-surface), 0.7); }
.ncrm-privacy-dashboard :deep(.ncrm-privacy-card__section) { margin: 0; font-size: 14px; font-weight: 700; }
.ncrm-privacy-dashboard :deep(.ncrm-privacy-dot) { display: inline-block; flex-shrink: 0; width: 10px; height: 10px; border-radius: 3px; }
.ncrm-privacy-dashboard :deep(.ncrm-privacy-legend) { display: flex; flex-wrap: wrap; gap: 14px; font-size: 12.5px; }
.ncrm-privacy-dashboard :deep(.ncrm-privacy-legend__item) { display: flex; align-items: center; gap: 6px; }

@media (max-width: 1100px) {
  .ncrm-privacy-dashboard__grid { grid-template-columns: 1fr; }
}
@media (max-width: 600px) {
  .ncrm-privacy-dashboard :deep(.ncrm-privacy-card) { padding: 18px 16px; }
}
</style>
